import { Box, Button, Flex } from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';

import { z } from 'zod/v4';
import { useCreditFacilityOrThrow } from '~/contexts/CreditFacilitiesProvider';
import { useFeatureFlags } from '~/contexts/FeatureFlagProvider';
import { RouteErrorBoundary } from '~/contexts/MonitoringProvider';
import { FinancialCovenantsContainer } from '~/packages/borrowing-base/ui/FinancialCovenantsContainer';
import { FinancialStatementsContainer } from '~/packages/financial-data/ui/financialStatements/FinancialStatementsContainer';
import { PortfolioTemplate } from '~/packages/portfolio/ui/shared/PortfolioTemplate';
// Types
const RouteParamsStruct = z.object({
  creditFacility: z.string({ message: 'Missing URL parameter' }),
});

const FinancialViewsIterable = [
  'Financial Covenants',
  'Financial Statements',
] as const;
type FinancialViewsTypes = typeof FinancialViewsIterable[number];

// Fragments
const RouteBase = () => {
  const [queryParams] = useSearchParams();
  const flags = useFeatureFlags();
  const [
    selectedFinancialView,
    setSelectedFinancialView,
  ] = useState<FinancialViewsTypes>('Financial Covenants');
  const { creditFacility: creditFacilityId } = RouteParamsStruct.parse(
    Object.fromEntries(queryParams),
  );

  useEffect(() => {
    setSelectedFinancialView('Financial Covenants');
  }, [creditFacilityId]);

  const creditFacility = useCreditFacilityOrThrow(creditFacilityId);

  return (
    <PortfolioTemplate
      creditFacilityId={creditFacility.id}
      creditFacilityName={creditFacility.name}
      selectedTab="financials"
    >
      <Flex direction="column" gap="2.5rem">
        <Flex gap="0.5rem">
          {FinancialViewsIterable.filter(
            (financialView) =>
              flags.isFinancialStatementsDashboardEnabled ||
              financialView !== 'Financial Statements',
          ).map((financialView) => (
            <Button
              key={financialView}
              variant="primary-muted"
              colorScheme="gray"
              isActive={selectedFinancialView === financialView}
              onClick={() => setSelectedFinancialView(financialView)}
            >
              {financialView}
            </Button>
          ))}
        </Flex>

        {selectedFinancialView === 'Financial Covenants' && (
          <FinancialCovenantsContainer creditFacilityId={creditFacility.id} />
        )}

        {selectedFinancialView === 'Financial Statements' && (
          <FinancialStatementsContainer creditFacilityId={creditFacility.id} />
        )}
      </Flex>
    </PortfolioTemplate>
  );
};

// Exports
export const Route = () => {
  return (
    <RouteErrorBoundary
      fallback={({ message, showErrorToast }) => {
        showErrorToast(message);
        return <Navigate to="/portfolio/overview/facilities" />;
      }}
    >
      <Box width="100%" padding="2.5rem">
        <RouteBase />
      </Box>
    </RouteErrorBoundary>
  );
};
